import { useId, useRef, useState, type FormEvent } from 'react';
import { AdminSpinner } from './AdminSpinner';
import { useTranslation } from 'react-i18next';
import { AiOutlineArrowRight } from 'react-icons/ai';
import type {
  AdminAuthStatus,
  TichChiAdminAuthController,
} from '../auth/admin-auth-controller';
import logo from '../static/images/logo.png';

export interface PasswordLoginPresentation {
  tone: 'neutral' | 'loading' | 'error';
  messageKey: string | null;
  submitting: boolean;
}

export function resolvePasswordLoginPresentation(
  authStatus: AdminAuthStatus,
): PasswordLoginPresentation {
  switch (authStatus) {
    case 'authenticating':
      return {
        tone: 'loading',
        messageKey: 'common:auth.password.verifying',
        submitting: true,
      };
    case 'denied':
      return {
        tone: 'error',
        messageKey: 'common:auth.password.denied',
        submitting: false,
      };
    case 'throttled':
      return {
        tone: 'error',
        messageKey: 'common:auth.password.throttled',
        submitting: false,
      };
    case 'expired':
      return {
        tone: 'error',
        messageKey: 'common:auth.password.expired',
        submitting: false,
      };
    case 'error':
      return {
        tone: 'error',
        messageKey: 'common:auth.password.networkError',
        submitting: false,
      };
    default:
      return {
        tone: 'neutral',
        messageKey: null,
        submitting: false,
      };
  }
}

export function AdminPasswordLogin({
  authController,
}: {
  authController: TichChiAdminAuthController;
}) {
  const { t } = useTranslation();
  const usernameId = useId();
  const passwordId = useId();
  const statusId = useId();
  const usernameRef = useRef<HTMLInputElement>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const authStatus = authController.extra.status;
  const presentation = resolvePasswordLoginPresentation(authStatus);
  const isError = presentation.tone === 'error';

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (presentation.submitting) return;
    await authController.signInWithPassword(username.trim(), password);
    if (usernameRef.current) {
      usernameRef.current.focus();
    }
  };

  return (
    <main className="admin-state-shell">
      <section
        className={`admin-login-card admin-state-panel--${presentation.tone}`}
        aria-labelledby="admin-login-title"
      >
        <header className="admin-login-heading">
          <img className="admin-login-logo" src={logo} alt="Tích Chỉ" />
          <h1 id="admin-login-title">{t('common:auth.title')}</h1>
        </header>
        <form
          className="admin-login-form"
          onSubmit={handleSubmit}
          data-admin-action="sign-in"
          noValidate
        >
          <div className="admin-login-field">
            <label htmlFor={usernameId}>
              {t('common:auth.password.usernameLabel')}
            </label>
            <input
              id={usernameId}
              ref={usernameRef}
              name="username"
              type="text"
              autoComplete="username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              disabled={presentation.submitting}
              aria-describedby={isError ? statusId : undefined}
              aria-invalid={isError || undefined}
              autoFocus
              required
            />
          </div>
          <div className="admin-login-field">
            <label htmlFor={passwordId}>
              {t('common:auth.password.passwordLabel')}
            </label>
            <input
              id={passwordId}
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={presentation.submitting}
              aria-describedby={isError ? statusId : undefined}
              aria-invalid={isError || undefined}
              required
            />
          </div>
          {presentation.messageKey ? (
            <div
              id={statusId}
              className={`admin-login-status admin-login-status--${presentation.tone}`}
              role={isError ? 'alert' : 'status'}
              aria-live={isError ? 'assertive' : 'polite'}
              aria-busy={presentation.submitting || undefined}
            >
              {presentation.submitting ? <AdminSpinner size="small" /> : null}
              <p>{t(presentation.messageKey)}</p>
            </div>
          ) : null}
          <button
            type="submit"
            className="admin-action-button"
            disabled={presentation.submitting}
          >
            <span>{t('common:auth.password.submit')}</span>
            <AiOutlineArrowRight aria-hidden="true" />
          </button>
        </form>
      </section>
    </main>
  );
}
